import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash, ArrowCounterClockwise, Image as ImageIcon } from '@phosphor-icons/react';
import * as technicianService from '../../../services/technicianService';

const INITIAL_VISIBLE_IMAGES = 10;

const normalizeExpectedTotalCount = (value) => {
    const parsedValue = Number(value);

    if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
        return null;
    }

    return parsedValue;
};

function ImageGallery({
    formId,
    taskId,
    technicianEmail,
    fieldName,
    expectedTotalCount = null,
    initialVisibleCount = INITIAL_VISIBLE_IMAGES,
    editable = false,
    onCountChange,
    pendingUploads = [],
    onQueueUpload,
    onDequeueUpload,
    pendingDeletes = [],
    onMarkDelete,
    onUnmarkDelete
}) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasImages, setHasImages] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(null);
    const fileInputRef = useRef(null);

    const normalizedExpectedTotalCount = useMemo(
        () => normalizeExpectedTotalCount(expectedTotalCount),
        [expectedTotalCount]
    );

    // Cargar la primera página de imágenes — se resetea el estado y se ignoran
    // respuestas de requests anteriores (cambio de campo/respuesta en curso)
    useEffect(() => {
        let cancelled = false;

        setImages([]);
        setError(null);
        setHasImages(true);
        setTotalCount(null);
        setIsExpanded(false);
        setLoadMoreError(null);
        setLoading(true);

        technicianService.getImagesByFolder({
            formId,
            taskId,
            technicianEmail,
            fieldName,
            offset: 0,
            pageSize: initialVisibleCount
        }).then((result) => {
            if (cancelled) return;

            if (result.success) {
                setImages(result.files);
                setTotalCount(Number.isInteger(result.totalCount) ? result.totalCount : null);
            }
        }).catch((err) => {
            if (cancelled) return;

            console.error('Error loading images:', err);
            if (err.message && err.message.includes('404')) {
                setHasImages(false);
            } else {
                setError('Error al cargar las imágenes');
            }
        }).finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [formId, taskId, technicianEmail, fieldName, initialVisibleCount]);

    const mergeUniqueImages = (currentImages, newImages) => {
        const imagesById = new Map(currentImages.map((image) => [image.id, image]));

        newImages.forEach((image) => {
            imagesById.set(image.id, image);
        });

        return Array.from(imagesById.values());
    };

    const loadImages = async (offset, pageSize = null) => {
        const result = await technicianService.getImagesByFolder({
            formId,
            taskId,
            technicianEmail,
            fieldName,
            offset,
            pageSize
        });

        if (result.success) {
            setImages((prev) => mergeUniqueImages(prev, result.files));
            setTotalCount(Number.isInteger(result.totalCount) ? result.totalCount : null);
        }

        return result;
    };

    // Corte por progreso real (imágenes únicas nuevas), no sólo por `totalCount`/página vacía:
    // si el backend no manda totalCount o repite/ignora el offset (p.ej. listados de Drive sin
    // orden estable), esas dos condiciones solas nunca se cumplen y el while quedaba pidiendo
    // GET /images indefinidamente al expandir.
    const loadRemainingImages = async () => {
        if (loadingMore) {
            return;
        }

        try {
            setLoadingMore(true);
            setLoadMoreError(null);
            setIsExpanded(true);

            const knownImages = new Map(images.map((image) => [image.id, image]));
            let currentOffset = knownImages.size;
            const resolvedTotal = Number.isInteger(totalCount)
                ? totalCount
                : normalizedExpectedTotalCount;

            while (resolvedTotal === null || currentOffset < resolvedTotal) {
                const result = await loadImages(currentOffset, initialVisibleCount);
                const loadedImages = Array.isArray(result?.files) ? result.files.length : 0;

                if (loadedImages === 0) {
                    break;
                }

                const sizeBeforeMerge = knownImages.size;
                result.files.forEach((image) => knownImages.set(image?.id, image));

                if (knownImages.size === sizeBeforeMerge) {
                    // La página no trajo ninguna imagen nueva (backend repitiendo/ignorando el
                    // offset): cortamos para no seguir pidiendo GET /images sin avanzar.
                    break;
                }

                currentOffset += loadedImages;
            }
        } catch (err) {
            console.error('Error loading more images:', err);
            setLoadMoreError('No se pudieron cargar las imágenes restantes.');
        } finally {
            setLoadingMore(false);
        }
    };

    const handleExpandImages = () => {
        if (isExpanded) {
            return;
        }

        if (Number.isInteger(totalCount) && images.length >= totalCount) {
            setIsExpanded(true);
            return;
        }

        loadRemainingImages();
    };

    // Total conocido (imágenes ya cargadas, total confirmado por backend, o el "expected"
    // que pase el padre como pista mientras carga) — usado para el conteo efectivo reportado
    // al padre y para decidir cuántas quedan ocultas detrás del tile "Ver restantes".
    const resolvedTotalImages = useMemo(() => {
        let total = images.length;

        if (Number.isInteger(totalCount) && totalCount >= 0) {
            total = Math.max(total, totalCount);
        }

        if (normalizedExpectedTotalCount !== null) {
            total = Math.max(total, normalizedExpectedTotalCount);
        }

        if (Number.isInteger(totalCount) && totalCount > images.length) {
            total = Math.max(total, initialVisibleCount + 1);
        }

        return total;
    }, [images.length, totalCount, normalizedExpectedTotalCount, initialVisibleCount]);

    const hiddenImagesCount = Math.max(0, resolvedTotalImages - initialVisibleCount);
    const displayedImages = isExpanded ? images : images.slice(0, initialVisibleCount);
    const shouldShowExpandOverlay = !isExpanded
        && hiddenImagesCount > 0
        && displayedImages.length === initialVisibleCount;

    // Informar al padre el conteo EFECTIVO de imágenes de este campo (persistidas - marcadas
    // para eliminar + pendientes de subir), usado para el badge "Sin completar" y para
    // bloquear el guardado en campos requeridos (ver FormResponseEditModal, R1 = Opción B).
    // Se usa resolvedTotalImages (no images.length) para que el conteo sea correcto aunque
    // la grilla esté colapsada mostrando menos imágenes de las que realmente existen.
    useEffect(() => {
        if (!onCountChange) return;
        const effectiveCount = resolvedTotalImages - pendingDeletes.length + pendingUploads.length;
        onCountChange(loading ? undefined : effectiveCount);
    }, [resolvedTotalImages, pendingDeletes.length, pendingUploads.length, loading, onCountChange]);

    const openFilePicker = () => fileInputRef.current?.click();

    // Subida diferida (open-questions.md B2 = Opción B): seleccionar un archivo ya no lo sube,
    // solo lo encola como pendiente en el estado del padre. La subida real ocurre recién al
    // presionar "Guardar Cambios" (ver FormResponseEditModal.handleSave).
    const handleFilesSelected = (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        onQueueUpload?.(files);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-4">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-amber"></div>
                <span className="ml-2 text-content-muted">Cargando imágenes...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded border border-error bg-error/12 px-4 py-3 text-error">
                {error}
            </div>
        );
    }

    const totalItems = images.length + pendingUploads.length;
    const isEmpty = (!hasImages || images.length === 0) && pendingUploads.length === 0;

    // Solo lectura y sin imágenes: mantener el mensaje simple que ya existía
    if (isEmpty && !editable) {
        return (
            <div className="rounded border border-line p-4 text-center text-content-muted">
                {hasImages ? 'No hay imágenes disponibles para esta carpeta' : 'Esta respuesta no posee imágenes'}
            </div>
        );
    }

    const fileInput = editable ? (
        <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
                handleFilesSelected(e.target.files);
                e.target.value = '';
            }}
        />
    ) : null;

    const showGrid = totalItems > 0;

    return (
        <div>
            {!showGrid ? (
                <div
                    onClick={openFilePicker}
                    className="border-2 border-dashed border-line rounded-lg p-8 flex flex-col items-center text-center gap-3 hover:border-amber transition-colors cursor-pointer"
                >
                    <ImageIcon size={32} className="text-content-muted" />
                    <p className="text-content font-medium">Sin imágenes cargadas</p>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber text-accent-text rounded-md text-xs font-medium hover:bg-amber/10 transition-colors"
                    >
                        <Plus size={14} /> Agregar imágenes
                    </button>
                    {fileInput}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                        {displayedImages.map((file, index) => {
                            const isExpandTile = shouldShowExpandOverlay && index === displayedImages.length - 1;
                            const markedForDelete = pendingDeletes.includes(file.id);

                            return (
                                <div key={file.id} className={`group relative ${markedForDelete ? 'opacity-40 grayscale' : ''}`}>
                                    <img
                                        src={`/api/storage/images/${file.id}/proxy`}
                                        alt={file.name}
                                        className="h-48 w-full cursor-pointer rounded-lg object-cover shadow-md transition-shadow hover:shadow-xl"
                                        onError={(e) => {
                                            console.error('Error cargando imagen:', file);
                                            e.target.style.display = 'none';

                                            const fallbackNode = e.currentTarget.parentElement?.querySelector('[data-image-fallback="true"]');
                                            if (fallbackNode) {
                                                fallbackNode.style.display = 'flex';
                                            }
                                        }}
                                    />
                                    {isExpandTile && (
                                        <button
                                            type="button"
                                            onClick={handleExpandImages}
                                            className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-black/70 text-white transition-colors hover:bg-black/80"
                                        >
                                            <span className="text-3xl font-semibold leading-none">+{hiddenImagesCount}</span>
                                            <span className="mt-2 text-xs font-medium uppercase tracking-[0.2em] text-content-muted">
                                                Ver restantes
                                            </span>
                                        </button>
                                    )}
                                    <div
                                        data-image-fallback="true"
                                        style={{ display: 'none' }}
                                        className="flex h-48 w-full flex-col items-center justify-center rounded-lg bg-surface-2 p-4 text-center shadow-md"
                                    >
                                        <p className="mb-2 text-sm text-error font-medium">Error al cargar</p>
                                        <p className="break-all text-xs text-content-muted">{file.name}</p>
                                        <p className="mt-2 text-xs text-accent-text">ID: {file.id}</p>
                                        {file.webViewLink && (
                                            <button
                                                type="button"
                                                onClick={() => window.open(file.webViewLink, '_blank')}
                                                className="mt-2 rounded bg-amber px-3 py-1 text-xs text-on-amber hover:bg-amber-hover"
                                            >
                                                Ver en Drive
                                            </button>
                                        )}
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 rounded-b-lg bg-black bg-opacity-75 p-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                                        {markedForDelete ? 'Marcada para eliminar' : file.name}
                                    </div>
                                    {editable && !isExpandTile && (markedForDelete ? (
                                        <button
                                            type="button"
                                            onClick={() => onUnmarkDelete?.(file.id)}
                                            title="Deshacer"
                                            aria-label="Deshacer eliminación"
                                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-surface/80 border border-amber/50 text-accent-text flex items-center justify-center opacity-100 transition-opacity hover:bg-amber hover:text-on-amber"
                                        >
                                            <ArrowCounterClockwise size={14} />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => onMarkDelete?.(file.id)}
                                            title="Eliminar imagen"
                                            aria-label="Eliminar imagen"
                                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-surface/80 border border-error/50 text-error flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-error hover:text-white"
                                        >
                                            <Trash size={14} />
                                        </button>
                                    ))}
                                </div>
                            );
                        })}

                        {isExpanded && loadingMore && (
                            <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-line bg-surface/50 text-sm text-content-muted">
                                Cargando imágenes restantes...
                            </div>
                        )}

                        {editable && pendingUploads.map((item) => (
                            <div key={item.id} className="relative group">
                                <img
                                    src={item.previewUrl}
                                    alt={item.file.name}
                                    className="w-full h-48 object-cover rounded-lg shadow-md opacity-80"
                                />
                                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide text-amber bg-amber/20 px-2 py-0.5 rounded-full">
                                    Pendiente de guardar
                                </span>
                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                    {item.file.name}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onDequeueUpload?.(item)}
                                    title="Quitar de la cola"
                                    aria-label="Quitar imagen pendiente"
                                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-surface/80 border border-error/50 text-error flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-error hover:text-white"
                                >
                                    <Trash size={14} />
                                </button>
                            </div>
                        ))}

                        {editable && (
                            <button
                                type="button"
                                onClick={openFilePicker}
                                className="w-full h-48 border-2 border-dashed border-line rounded-lg flex flex-col items-center justify-center gap-2 text-content-muted hover:border-amber hover:text-accent-text transition-colors"
                            >
                                <Plus size={22} />
                                <span className="text-xs font-medium">Agregar</span>
                            </button>
                        )}
                    </div>
                    {fileInput}
                </>
            )}

            {loadMoreError && (
                <div className="mt-4 rounded-lg border border-error bg-error/12 px-4 py-3 text-sm text-error">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <span>{loadMoreError}</span>
                        <button
                            type="button"
                            onClick={loadRemainingImages}
                            className="rounded-md bg-error px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-error/80"
                        >
                            Reintentar
                        </button>
                    </div>
                </div>
            )}

            {isExpanded && resolvedTotalImages > initialVisibleCount && (
                <div className="mt-4 text-center">
                    <button
                        type="button"
                        onClick={() => setIsExpanded(false)}
                        disabled={loadingMore}
                        className="rounded-lg bg-surface-2 px-4 py-2 text-content transition-colors hover:bg-line disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Mostrar menos
                    </button>
                </div>
            )}
        </div>
    );
}

export default ImageGallery;
